import { QuoteBlockedReasonItemLevel } from '@brenger/api-client';
import { IconPencilOutline } from '@brenger/react/icons/IconPencilOutline';
import { getWeightRangeForValue } from '@brenger/utils';
import cn from 'classnames';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import '../../../../assets/product-selection/itemDisplay.scss';
import { Button, FlashMessage, IconCamera, IconMinus, IconPlus } from '../../../../brenger-shared-ui';
import { IconTrash2 } from '../../../../brenger-shared-ui/components/basics/icons/IconTrash2';
import { r } from '../../../../routes';
import { userDataClient } from '../../../../utils/request';
import { actions } from '../../ducks';
import { ItemWithPSAndLot } from '../../interface';
import { BlockedReasonsList } from '../BlockedReasons';

interface Props {
  item: ItemWithPSAndLot;
}

export const ItemDisplay: React.FC<Props & { blockedReasons: QuoteBlockedReasonItemLevel[] }> &
  React.DOMAttributes<HTMLBaseElement> = ({ item, blockedReasons, ...unused }) => {
  const history = useHistory();
  return (
    <div className="card card--bordered ps--item-display mb-4" {...unused}>
      <div className={cn('flex', { 'pb-1': blockedReasons.length > 0 })}>
        <ItemDisplayImage image={item.job_image} />
        <div style={{ flexGrow: 1 }}>
          <div className="ps--item-display--title">
            <Button
              type="button"
              extraClasses="ps--item-display--edit"
              buttonStyle="transparant"
              onClick={() => history.push(r.generalFlow.items.edit({ uuid: item.ps_temp.uuid as string }))}
            >
              {item.ps_temp.fullTitle}
              <IconPencilOutline />
            </Button>
          </div>
          <div className="text-grey-500 mb-2">
            {item.length} x {item.width} x {item.height} cm{' '}
            {item.weight_range && `| ${getWeightRangeForValue(item.weight_range.min).label}`}
          </div>
          <ItemDisplayCount item={item} />
        </div>
      </div>
      {blockedReasons.length > 0 && (
        <FlashMessage type={'error'}>
          <BlockedReasonsList reasons={blockedReasons} />
        </FlashMessage>
      )}
    </div>
  );
};

const ItemDisplayCount: React.FC<Props> = ({ item }) => {
  const dispatch = useDispatch();
  const { count } = item;
  const update = (newCount: number): void => {
    dispatch(actions.getPrice());
    if (newCount > 0) {
      dispatch(
        actions.updateItemPs({
          uuid: item.ps_temp.uuid as string,
          item: {
            ...item,
            count: newCount,
          },
        })
      );
      return;
    }
    dispatch(actions.removeItemPs({ uuid: item.ps_temp.uuid as string }));
  };
  return (
    <div className={'ps-config--count ps--item-display--counter'}>
      <div className="ps-config--count-value flex">
        <Button
          type={'button'}
          buttonStyle={'transparant'}
          onClick={() => update(count - 1)}
          extraClasses={cn('!flex items-center ps-config--count-trigger', {
            'ps-config--count--delete': count < 2,
            'ps-config--count--decrease': count > 1,
          })}
        >
          {count === 1 ? <IconTrash2 className="text-[#e10000]" /> : <IconMinus />}
        </Button>
        <div className="ps-config--count--number">{count}</div>
        <Button
          onClick={() => update(count + 1)}
          type={'button'}
          buttonStyle={'transparant'}
          extraClasses="!flex items-center ps-config--count-trigger ps-config--count--increase"
        >
          <IconPlus />
        </Button>
      </div>
    </div>
  );
};

const ItemDisplayImage: React.FC<{ image?: string | null }> = ({ image }) => {
  if (image) {
    return <img className="ps--item-display--image" src={userDataClient.getBaseForIri(image, { h: 160, w: 160 })} />;
  }
  return (
    <div className="ps--item-display--no-image flex justify-center items-center">
      <IconCamera />
    </div>
  );
};
